import classNames from "classnames"
import { renderRichText } from "gatsby-source-storyblok"
import { motion } from "motion/react"

import AccordionPlusIcon from "./Icons/AccordionPlusIcon"
import Typography from "./Typography"
import AccordionArrowIcon from "../../../../src/components/elements/V2/Icons/AccordionArrowIcon"
import { Color } from "../../../constants/V2/color"

import { getTextColorClass } from "@utils/V2/color"
import React, { ComponentProps, useState } from "react"

type Props = {
  title: string
  body?: string | Storyblok.StoryblokRichtextContent
  textColor: Color
  iconColor: Color
  icon?: "arrow" | "plus"
  hideIcon?: boolean
  disabled?: boolean
  titleFontWeight?: ComponentProps<typeof Typography>["weight"]
  titleSize?: ComponentProps<typeof Typography>["size"]
  contentLeftPadding?: boolean
  contentTopPadding?: boolean
  children?: React.ReactNode
  openByDefault?: boolean
  closeOn?: "title" | "any"
  className?: string
  titleClassName?: string
}

const Accordion = ({
  title,
  textColor,
  body,
  iconColor,
  icon = "plus",
  hideIcon = false,
  disabled = false,
  titleFontWeight = "medium",
  titleSize = "body-lg",
  contentLeftPadding = false,
  contentTopPadding = true,
  children,
  openByDefault = false,
  closeOn = "any",
  className,
  titleClassName,
}: Props) => {
  const [isOpen, setIsOpen] = useState(openByDefault)

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  return (
    <div
      className={classNames(
        "grid cursor-pointer grid-cols-[1fr_max-content] items-start gap-x-16",
        className
      )}
    >
      <button disabled={disabled} className="text-left" onClick={onClick}>
        <Typography
          font="grotesk"
          color={textColor}
          text={title}
          size={titleSize}
          weight={titleFontWeight}
          className={titleClassName}
        />
      </button>

      {hideIcon ? null : (
        <button onClick={closeOn === "any" ? onClick : undefined}>
          {icon === "plus" ? (
            <AccordionPlusIcon color={iconColor} open={isOpen} />
          ) : (
            <AccordionArrowIcon open={isOpen} color={iconColor} />
          )}
        </button>
      )}
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0 }}
        transition={{ duration: 0.1, ease: "easeInOut" }}
        className={classNames(
          "col-span-2 flex-nowrap",
          !isOpen ? "overflow-hidden" : "overflow-visible",
          { "pl-10 sm:pl-20": contentLeftPadding },
          { "pt-16": contentTopPadding }
        )}
      >
        {typeof body !== "string" ? (
          <div
            className={classNames(
              getTextColorClass(textColor),
              "text-responsive-body-md font-grotesk font-normal",
              "space-y-20 [&_*]:!text-current [&_a]:!text-blue"
            )}
            dangerouslySetInnerHTML={{ __html: renderRichText(body) ?? "" }}
          />
        ) : (
          <Typography
            text={body}
            font="grotesk"
            size="body-md"
            weight="book"
            color={textColor}
          />
        )}
        {children}
      </motion.div>
    </div>
  )
}

export default Accordion
